import React, { useEffect, useMemo, useRef } from "react";
import { FaPaperPlane } from "react-icons/fa6";
import { useFormik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import { formatEnDate } from "../../global/date.util";
import { useNavigate } from "react-router-dom";
import {
	useRetryIRDBill,
	useAddCreditNote,
	BillSubmitError,
	handleBillSubmitError,
} from "../../queries/bill.queries";
import type { Bill, AddCreditNoteVars } from "../../queries/bill.queries";
import InputDatePicker from "../input-date-picker/input-date-picker.component";
import FormError from "../form-error/form-error.component";
import SuccessDisplay from "../success-display/success-display.component";

interface CreditNoteFormProps {
	selectedBill: Bill;
	companyDataCorrupt?: boolean;
	onCancel: () => void;
	onSuccess: () => void;
	// Reports live dirty state up to the parent, which owns the
	// StructuredSideModal this renders inside — lets it show a
	// discard-changes confirm before closing (see invoice-bill.page.tsx).
	onDirtyChange?: (isDirty: boolean) => void;
}

type CreditDraft = AddCreditNoteVars;

// Extracted from invoice-bill.page.jsx's former ~100-line inline
// _renderSideModalContent branch — self-contained: owns its own formik and
// the addCreditNote/retryIRDBill mutations it needs, taking only the bill
// it's filing a credit note against plus cancel/success callbacks.
function CreditNoteForm({ selectedBill, companyDataCorrupt, onCancel, onSuccess, onDirtyChange }: CreditNoteFormProps) {
	const navigate = useNavigate();
	const retryMutation = useRetryIRDBill();
	const addCreditNoteMutation = useAddCreditNote();

	// Brief SuccessDisplay before closing the panel — same ~1.2s pattern as
	// customer-bulk-draft/bill-duplicate's useSaveDraft consumers.
	const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
		};
	}, []);

	// Recreated when the referenced bill's invoice_date changes (i.e. per
	// selection) rather than a module-level constant, since the "not earlier
	// than the invoice date" test closes over it directly.
	const createCreditNoteSchema = useMemo(
		() =>
			yup.object().shape({
				credit_note_date: yup
					.string()
					.required("Required")
					.test(
						"is-later-than-last-bill-date",
						"Date must not be earlier than the invoice date",
						(value) => {
							if (selectedBill?.invoice_date) {
								return new Date(value ?? "") >= new Date(selectedBill.invoice_date);
							}
							return true;
						},
					),
				reason_for_return: yup.string().required("Required"),
			}),
		[selectedBill?.invoice_date],
	);

	const formik = useFormik({
		validationSchema: createCreditNoteSchema,
		validateOnMount: false,
		initialValues: {
			credit_note_date: formatEnDate(new Date(), "YYYY-MM-DD"),
			reason_for_return: "",
		},
		onSubmit: async (values) => {
			if (companyDataCorrupt) return;
			const creditDraft: CreditDraft = {
				...selectedBill,
				ref_invoice_number: selectedBill?.invoice_number,
				...values,
			};

			try {
				await addCreditNoteMutation.mutateAsync(creditDraft);
				formik.resetForm();
				closeTimeoutRef.current = setTimeout(() => onSuccess(), 1200);
			} catch (error) {
				if (error instanceof BillSubmitError && error.code === 95) {
					// Retry flow renders its own Swal — clear the mutation's error
					// state so FormError doesn't also flash underneath it.
					addCreditNoteMutation.reset();
					handleCreditNoteFailure(creditDraft);
				} else if (error instanceof BillSubmitError) {
					// Navigating to /error's ERROR_MAP, which already explains this
					// code — same reasoning as direct-bill.component.tsx not
					// rendering FormError for any useAddBill() failure.
					addCreditNoteMutation.reset();
					handleBillSubmitError(error, navigate);
				}
				// Any other error (network/validation) stays on this screen and
				// renders via FormError, bound to addCreditNoteMutation below.
			}
		},
	});

	useEffect(() => {
		onDirtyChange?.(formik.dirty);
	}, [formik.dirty, onDirtyChange]);

	// A referenced bill wasn't actually synced with IRD yet — retry that sync,
	// then retry the credit note creation once it succeeds.
	function handleCreditNoteFailure(creditDraftToBeRetry: CreditDraft) {
		Swal.fire({
			title: "Bill not synced",
			text: `Sync invoice ${creditDraftToBeRetry?.ref_invoice_number} with IRD and create credit note.`,
			showDenyButton: true,
			confirmButtonText: "Sync and create",
			denyButtonText: "Cancel",
			customClass: {
				popup: "alert-modal",
				actions: "btn-container",
				confirmButton: "alert-btn btn-confirm",
				denyButton: "alert-btn btn-cancel",
			},
		}).then(async (click) => {
			if (click.isConfirmed) {
				try {
					await retryMutation.mutateAsync({ bill_id: creditDraftToBeRetry.bill_id });
					await addCreditNoteMutation.mutateAsync(creditDraftToBeRetry);
					formik.resetForm();
					closeTimeoutRef.current = setTimeout(() => onSuccess(), 1200);
				} catch {
					// Sync or resubmit failed again — nothing further to do here;
					// the user can retry from this same form.
				}
			}
		});
	}


	const handleCancel = () => {
		formik.resetForm();
		onCancel();
	};

	if (addCreditNoteMutation.isSuccess) {
		return (
			<div className="credit-note-form">
				<SuccessDisplay
					title="Credit note created"
					description={`Credit note generated for ${selectedBill?.invoice_number}.`}
				/>
			</div>
		);
	}

	return (
		<div className="credit-note-form">
			<h2 className="section-header">Create Credit Note</h2>
			<FormError
				isError={addCreditNoteMutation.isError}
				error={addCreditNoteMutation.error}
				fallback="Failed to create credit note."
			/>
			<div className="input-form-container">
				<div className="input-row">
					<div className="input-item">
						<p className="input-label">Reference Invoice Number</p>
						<div className="input-box-wrapper">{selectedBill?.invoice_number}</div>
					</div>
					<div className="input-item">
						<p className="input-label">Credit Note Date</p>
						<InputDatePicker
							value={
								formik.values.credit_note_date
									? new Date(formik.values.credit_note_date)
									: null
							}
							onChange={(result) =>
								formik.setFieldValue(
									"credit_note_date",
									formatEnDate(result.en, "YYYY-MM-DD"),
								)
							}
							minDate={
								selectedBill?.invoice_date
									? new Date(selectedBill.invoice_date)
									: undefined
							}
							disabled
							error={
								formik.submitCount > 0
									? formik.errors.credit_note_date
									: undefined
							}
						/>
					</div>
				</div>
				<div className="input-row">
					<div className="input-item">
						<p className="input-label">Reasons for Credit Note Creation</p>
						<textarea
							name="reason_for_return"
							className="textarea-wrapper"
							rows={4}
							placeholder="Write a reason for return..."
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values?.reason_for_return}
						/>
						{formik.submitCount > 0 && (
							<div className="validation-error">{formik.errors.reason_for_return}</div>
						)}
					</div>
				</div>
			</div>
			{companyDataCorrupt && (
				<div className="action-warn">
					There are
					<span style={{ fontWeight: "bold" }}> technical errors with your IRD account. </span>
					Bills and Credit Notes cannot be generated. Please contact your service providers to resolve
					this issue.
				</div>
			)}
			<div className="action-container">
				<div className="action-btn inverted" onClick={handleCancel}>
					Cancel
				</div>
				<div
					className="action-btn"
					onClick={() => formik.submitForm()}
					aria-disabled={
						formik.isSubmitting ||
						companyDataCorrupt ||
						addCreditNoteMutation.isPending ||
						retryMutation.isPending
					}
				>
					<FaPaperPlane />
					Send Request
				</div>
			</div>
		</div>
	);
}

export default CreditNoteForm;
